import React, { useCallback, useState } from 'react';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import EnhancedBusinessBrandingPanel from '../../settings/EnhancedBusinessBrandingPanel';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import type { Routemize_DTOs_BusinessBrandingDTO } from '../../../api/models/Routemize_DTOs_BusinessBrandingDTO';
import { Disclosure, StepIntro } from '../shared/StepShell';

/**
 * The branding panel publishes its save routine on `window` when rendered
 * inline. That contract belongs to the panel, so it is read through one typed
 * accessor here rather than being spread across the wizard.
 */
const getBrandingSave = (): (() => Promise<void>) | undefined =>
  (window as unknown as { __enhancedBusinessBrandingSave?: () => Promise<void> })
    .__enhancedBusinessBrandingSave;

const isLightColor = (hex: string): boolean => {
  const value = hex.replace('#', '');
  if (value.length < 6) return false;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
};

/**
 * Static mock of the customer-facing booking page. Deliberately still: the
 * previous preview auto-rotated tabs and cycled date selections on timers,
 * which pulled attention away from the form the user is meant to be filling.
 */
const BrandPreview: React.FC<{
  businessName?: string | null;
  primaryColor?: string | null;
  logoUrl?: string | null;
}> = ({ businessName, primaryColor, logoUrl }) => {
  const name = businessName?.trim() || 'Your business';
  const brand = primaryColor || '#4f46e5';
  const onBrand = isLightColor(brand) ? '#1a1a1a' : '#ffffff';

  return (
    <div className="mx-auto max-w-xs overflow-hidden rounded-lg border border-ink-200 bg-surface">
      <div className="p-3">
        <div className="mb-2 flex justify-center">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-8 max-w-[96px] object-contain" />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold"
              style={{ backgroundColor: brand, color: onBrand }}
            >
              {name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <p className="text-center text-xs font-bold text-ink-900">
          Schedule with <span style={{ color: brand }}>{name}</span>
        </p>
        <p className="mb-2.5 mt-0.5 text-center text-2xs text-ink-500">
          Pick a day that works for you
        </p>

        <div className="mb-2.5 flex gap-1.5">
          {['Tue 14', 'Wed 15', 'Thu 16'].map((slot, index) => (
            <div
              key={slot}
              className="flex-1 rounded-md border border-ink-200 p-1.5 text-center"
              style={
                index === 0 ? { borderColor: brand, backgroundColor: `${brand}14` } : undefined
              }
            >
              <p className="text-2xs font-semibold text-ink-800">{slot.split(' ')[0]}</p>
              <p className="text-sm font-bold text-ink-900">{slot.split(' ')[1]}</p>
            </div>
          ))}
        </div>

        <div
          className="rounded-md py-1.5 text-center text-2xs font-semibold"
          style={{ backgroundColor: brand, color: onBrand }}
        >
          Confirm booking
        </div>
      </div>
      <p className="border-t border-ink-100 bg-ink-50 py-1 text-center text-9 text-ink-400">
        Powered by <span className="font-semibold text-ink-500">routemize</span>
      </p>
    </div>
  );
};

export const BrandStep: React.FC = () => {
  const { completeStep } = useOnboarding();
  const [brand, setBrand] = useState<Routemize_DTOs_BusinessBrandingDTO>({});

  useStepValidity((brand.businessName ?? '').trim().length >= 2);

  const handleDataChange = useCallback((data: Routemize_DTOs_BusinessBrandingDTO) => {
    setBrand(data);
  }, []);

  useStepSaver(
    ONBOARDING_STEP.BRAND,
    useCallback(async () => {
      const save = getBrandingSave();
      if (!save) {
        throw new Error('The branding form is still loading. Please try again in a moment.');
      }
      await save();
      completeStep(ONBOARDING_STEP.BRAND);
    }, [completeStep])
  );

  return (
    <div className="space-y-4">
      <StepIntro>
        This is what customers see on your booking page. Add your website and we'll pull in your
        logo and colors automatically.
      </StepIntro>

      <div className="rounded-xl border border-ink-200 bg-surface">
        <EnhancedBusinessBrandingPanel
          isOpen
          onClose={() => {}}
          inline
          onDataChange={handleDataChange}
        />
      </div>

      {/* Collapsed by default so the preview never competes with the fields
          above it on a short screen. */}
      <Disclosure summary="Preview your booking page">
        <BrandPreview
          businessName={brand.businessName}
          primaryColor={brand.primaryColor}
          logoUrl={brand.logoPath}
        />
      </Disclosure>
    </div>
  );
};
